import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, Save, RotateCcw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

const toText = (row) => (row.override.length ? row.override : row.pricing).join("\n");

/** Admin control for the plan-change email's {{features_block}} list, per tier. */
export const PlanFeaturesPanel = () => {
  const [rows, setRows] = useState(null);
  const [limit, setLimit] = useState(7);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const apply = useCallback((tiers, displayLimit) => {
    setRows(tiers);
    if (displayLimit) setLimit(displayLimit);
    setDrafts(Object.fromEntries(tiers.map((r) => [r.tier_key, toText(r)])));
  }, []);

  useEffect(() => {
    api.get("/admin/email-templates/plan-features")
      .then(({ data }) => apply(data.tiers, data.display_limit))
      .catch(() => toast.error("Could not load plan feature lists."));
  }, [apply]);

  const save = async (key) => {
    setBusy(key);
    try {
      const features = (drafts[key] || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put(`/admin/email-templates/plan-features/${key}`, { features });
      apply(data.tiers);
      toast.success("Saved — new upgrade emails will use this list.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(""); }
  };

  const reset = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.delete(`/admin/email-templates/plan-features/${key}`);
      apply(data.tiers);
      toast.success("Back to your Pricing features.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset."); }
    finally { setBusy(""); }
  };

  if (!rows) return null;

  return (
    <Card className="mt-6 p-5" data-testid="plan-features-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-700"><ListChecks className="h-5 w-5" /></span>
          <div>
            <h2 className="text-lg font-[680] text-foreground">Plan features in emails</h2>
            <p className="max-w-3xl text-sm text-muted-foreground">
              This is what <code className="rounded bg-secondary px-1 font-mono text-[11px]">{"{{features_block}}"}</code> prints in the
              plan-change email. By default it follows your live Pricing cards — the first {limit} visible Included or Add-on
              features, with <em>(beta)</em> and <em>(coming soon)</em> labels kept. Edit a box below to word it just for email;
              clear it or hit Reset to follow Pricing again.
            </p>
          </div>
        </div>
        <Link to="/dashboard/admin/pricing" className="text-sm font-[560] text-primary hover:underline" data-testid="plan-features-pricing-link">
          Edit Pricing features <ExternalLink className="ml-0.5 inline h-3.5 w-3.5" />
        </Link>
      </div>

      <Separator className="my-4" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {rows.map((r) => {
          const dirty = (drafts[r.tier_key] || "") !== toText(r);
          return (
            <div key={r.tier_key} className="rounded-lg border border-border p-4" data-testid={`plan-features-${r.tier_key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-[640] text-foreground">{r.tier_name}</span>
                <Badge variant="secondary" className="text-[10px]" data-testid={`plan-features-source-${r.tier_key}`}>
                  {r.source === "override" ? "Custom for email" : "From Pricing"}
                </Badge>
              </div>
              <Textarea
                rows={7}
                className="mt-2 font-mono text-xs"
                value={drafts[r.tier_key] ?? ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [r.tier_key]: e.target.value }))}
                placeholder="One feature per line"
                data-testid={`plan-features-input-${r.tier_key}`}
              />
              <div className="mt-2 rounded-md bg-secondary/50 p-3" data-testid={`plan-features-preview-${r.tier_key}`}>
                <div className="text-[10px] font-[700] uppercase tracking-[0.1em] text-muted-foreground">Email will show</div>
                <ul className="mt-1.5 space-y-1">
                  {r.resolved.map((f) => (
                    <li key={f} className="text-xs text-foreground"><span className="mr-1.5 font-[700] text-primary">✓</span>{f}</li>
                  ))}
                  {r.resolved.length === 0 && <li className="text-xs text-muted-foreground">Nothing — the block is skipped.</li>}
                  {r.truncated && <li className="text-xs text-muted-foreground">…and more!</li>}
                </ul>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" onClick={() => save(r.tier_key)} disabled={!dirty || busy === r.tier_key} data-testid={`plan-features-save-${r.tier_key}`}>
                  <Save className="mr-1 h-3.5 w-3.5" /> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => reset(r.tier_key)} disabled={r.source !== "override" || busy === r.tier_key} data-testid={`plan-features-reset-${r.tier_key}`}>
                  <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset to Pricing
                </Button>
                {dirty && <span className="text-xs text-amber-600">Unsaved</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
